import { FC, ReactNode } from 'react';

import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { formatDate } from '../../utils/delivery.utils';
import { RebateRowCalc, RebateTotals } from '../../utils/rebate-table.utils';

/**
 * 금액 표기. 수수료·수익금이 건별 반올림된 정수라 소수는 원래 없지만,
 * 혹시 남은 값(개정 전 데이터의 판매가/원가 등)도 버리지 않고 그대로 보여준다.
 */
const formatNum = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 6 });

// ─────────────────────────────────────────────
// RebateSettlementTable
//
// 상조 리베이트 정산 공용 테이블 (정산 예정 / 배송완료 / 완료 상세 공유).
// 계산은 rebate-table.utils 의 computeRebateRow/computeRebateTotals 가 담당하고
// 이 컴포넌트는 렌더링만 한다.
//
// 미정산(집행 대상 선택) 화면에서는 selection 으로 행 체크박스를,
// header 로 그룹 헤더(상조명·집행 버튼 등)를 카드 안에 함께 끼워 넣는다.
// ─────────────────────────────────────────────

/** 행 선택(집행 대상) 제어 */
export interface RebateSettlementTableSelection {
  /** 선택된 행 키(=RebateRowCalc.rowKey) 집합 */
  selectedKeys: Set<string>;
  /** 행 토글 */
  onToggleRow: (key: string) => void;
}

/** `RebateSettlementTable` 컴포넌트의 props */
export interface RebateSettlementTableProps {
  /** 계산된 행 목록 */
  rows: RebateRowCalc[];
  /** 합계 */
  totals: RebateTotals;
  /** 행이 없을 때 표시할 메시지 */
  emptyMessage: string;
  /** 카드 상단에 끼워 넣을 헤더(미정산 그룹 헤더 등) */
  header?: ReactNode;
  /** 고정 너비 레이아웃(table-fixed + colgroup) — 내용에 따라 열 너비가 출렁이지 않게 한다 */
  fixedLayout?: boolean;
  /** 행 선택(체크박스) 제어. 지정 시 맨 앞에 선택 칼럼이 추가된다 */
  selection?: RebateSettlementTableSelection;
  /** 하단 계산식 안내 문구 표시 (기본 true) */
  showFootnote?: boolean;
  /** 수수료 칼럼 헤더에 표기할 요율(%) — 상조별 feeRate. 미지정 시 요율 표기를 생략한다 */
  feeRate?: number;
}

export const RebateSettlementTable: FC<RebateSettlementTableProps> = ({
  rows,
  totals,
  emptyMessage,
  header,
  fixedLayout = false,
  selection,
  showFootnote = true,
  feeRate,
}) => {
  const selectable = !!selection;
  // 합계 첫 셀 colSpan: 순번/업체·상품/구매자/배송/고인/판매가·원가 = 6칸 (+ 선택칼럼 1칸)
  const firstColSpan = selectable ? 7 : 6;

  // footer는 건별 정수(수수료 올림·지급액 내림)의 합 — 서버 정산 총액과 동일하고
  // "매출 − 원가 − 수수료 = 수익금"이 건별 항등식으로 정확히 맞아떨어진다.

  return (
    <div className="mb-6">
      <div className="overflow-x-auto rounded-lg bg-white shadow">
        {header}
        {rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
        ) : (
          <table
            className={`min-w-full divide-y divide-gray-200 text-sm${
              fixedLayout ? ' table-fixed' : ''
            }`}
          >
            {fixedLayout && (
              <colgroup>
                {selectable && <col className="w-10" />}
                <col className="w-[5%]" />
                <col className="w-[13%]" />
                <col className="w-[6%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[5%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
              </colgroup>
            )}
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                {selectable && <th className="px-3 py-2" />}
                <th className="px-3 py-2 text-center text-sm">순번</th>
                <th className="px-3 py-2 text-left">
                  <p className="text-xs">업체명</p>
                  <p className="mt-0.5 text-xs">상품명</p>
                </th>
                <th className="px-3 py-2 text-left text-sm">구매자</th>
                <th className="px-3 py-2 text-left">
                  <p className="text-xs">배송완료일</p>
                  <p className="mt-0.5 text-xs">배송요청일</p>
                </th>
                <th className="px-3 py-2 text-left">
                  <p className="text-xs">고인명</p>
                  <p className="mt-0.5 text-xs">발인일</p>
                </th>
                <th className="px-3 py-2 text-right">
                  <p className="text-xs">판매가(개당)</p>
                  <p className="mt-0.5 text-xs">원가(개당)</p>
                </th>
                <th className="px-3 py-2 text-right text-sm">수량</th>
                <th className="px-3 py-2 text-right text-sm">결제금액</th>
                <th className="px-3 py-2 text-right text-sm">상품원가</th>
                <th className="px-3 py-2 text-right text-sm">
                  수수료
                  {feeRate != null && (
                    <span className="text-xs font-normal text-gray-400">
                      {' '}
                      ({feeRate}%)
                    </span>
                  )}
                </th>
                <th className="px-3 py-2 text-right text-sm">수익금</th>
                <th className="px-3 py-2 text-left">
                  <p className="text-xs">지도사</p>
                  <p className="mt-0.5 text-xs">연락처</p>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, idx) => {
                const key = row.rowKey;
                const isSel =
                  selectable && key != null && selection.selectedKeys.has(key);
                return (
                  <tr
                    key={key ?? idx}
                    className={`hover:bg-gray-50 ${isSel ? 'bg-indigo-50' : ''}`}
                    onClick={
                      selectable && key != null
                        ? () => selection.onToggleRow(key)
                        : undefined
                    }
                    style={{ cursor: selectable ? 'pointer' : undefined }}
                  >
                    {selectable && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={!!isSel}
                          readOnly
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-3 text-center text-xs tabular-nums text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-[11px] text-gray-500">
                        {row.vendorName || '-'}
                      </p>
                      <p className="mt-0.5 break-words font-medium">
                        {row.itemName}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs font-bold text-gray-700">
                      {row.receiverName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      <p className="font-bold text-gray-700">
                        {formatDate(row.deliveredAt)}
                      </p>
                      <p className="mt-0.5">{formatDate(row.submittedAt)}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      <p>{row.deceasedName || '-'}</p>
                      <p className="mt-0.5 font-bold text-gray-700">
                        {row.coffinOutDate ? formatDate(row.coffinOutDate) : '-'}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right text-xs tabular-nums">
                      <p className="font-bold text-gray-700">
                        {row.priceRetail != null ? formatNum(row.priceRetail) : '-'}
                      </p>
                      <p className="mt-0.5 text-gray-500">
                        {row.priceWhole != null ? formatNum(row.priceWhole) : '-'}
                        {row.extraUnitPrice ? (
                          <span className="ml-1 text-[10px] text-amber-600">
                            소량+{row.extraUnitPrice.toLocaleString()}
                          </span>
                        ) : null}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {row.quantity}개
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {row.priceRetail != null
                        ? formatNum(row.priceRetail * row.quantity)
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {row.priceWhole != null
                        ? formatNum(row.priceWhole * row.quantity)
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {row.lineFee != null ? formatNum(row.lineFee) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <span className="font-semibold tabular-nums text-gray-800">
                        {row.lineAmount != null ? formatNum(row.lineAmount) : '-'}
                      </span>
                      {!row.hasSettlementInfo && (
                        <span className="block text-[10px] text-amber-600">
                          정산정보 미설정
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      <p>{row.workerName || '-'}</p>
                      {row.workerPhoneNumber && (
                        <p className="mt-0.5 text-gray-400">
                          {phoneNumberStringFormator(row.workerPhoneNumber)}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={firstColSpan}>
                  합계 ({totals.totalCount}건)
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {totals.totalQuantity}개
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-700">
                  {formatNum(totals.totalPriceRetail)}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-700">
                  {formatNum(totals.totalPriceWhole)}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-700">
                  {formatNum(totals.totalFee)}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatNum(totals.totalAmount)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
      {showFootnote && rows.length > 0 && (
        <div className="mt-2 space-y-0.5 text-xs text-gray-400">
          <p>* 결제금액 = 판매가 × 수량</p>
          <p>* 수수료는 건별 원 단위 올림, 수익금(지급액)은 건별 원 단위 내림 — 합계는 건별 값의 합</p>
          {rows.some((r) => r.extraUnitPrice) && (
            <p>* 원가(개당)는 소량주문 추가단가(소량+)를 포함합니다</p>
          )}
        </div>
      )}
    </div>
  );
};
